import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { contactsApi } from "@/services/api";
import type { Contact } from "@/types";
import {
  Search, Plus, Phone, Mail, Building2, ChevronRight,
  Loader2, AlertCircle, Upload, Trash2, CheckSquare,
  Users, ArrowUpDown, MapPin, X, FileClock,
} from "lucide-react";
import { formatTimeAgo } from "@/utils/timeAgo";
import { VcfUpload } from "@/components/VcfUpload";

interface ContactWithPending extends Contact {
  pending_proposals_count?: number;
  is_pending_create?: boolean;
  proposal_id?: number;
  latest_pending_action?: string;
}

type SortOption = 'name_asc' | 'name_desc' | 'company_asc' | 'company_desc' | 'updated_desc' | 'updated_asc' | 'created_desc';

const sortLabels: Record<SortOption, string> = {
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
  company_asc: 'Firma A–Z',
  company_desc: 'Firma Z–A',
  updated_desc: 'Zuletzt aktualisiert',
  updated_asc: 'Älteste Aktualisierung',
  created_desc: 'Neueste Erstellung',
};

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const SORT_STORAGE_KEY = 'contacts_sort_v1';

function getChangedFields(contact: ContactWithPending): Array<{label: string, oldVal: string, newVal: string}> {
  if (!contact.pending_proposal_data) return [];
  const proposed = contact.pending_proposal_data;
  if (contact.latest_pending_action === 'delete') return [];
  const fields: Array<{key: keyof Contact, label: string}> = [
    {key: 'first_name', label: 'Vorname'},
    {key: 'last_name', label: 'Nachname'},
    {key: 'display_name', label: 'Anzeigename'},
    {key: 'title', label: 'Titel'},
    {key: 'email_address', label: 'E-Mail'},
    {key: 'secondary_email', label: 'E-Mail 2'},
    {key: 'tertiary_email', label: 'E-Mail 3'},
    {key: 'company_name', label: 'Firma'},
    {key: 'department', label: 'Abteilung'},
    {key: 'job_title', label: 'Position'},
    {key: 'mobile_phone', label: 'Mobil'},
    {key: 'business_phone', label: 'Geschäftlich'},
    {key: 'business_phone_2', label: 'Geschäftlich 2'},
    {key: 'home_phone', label: 'Privat'},
    {key: 'home_phone_2', label: 'Privat 2'},
    {key: 'fax', label: 'Fax'},
    {key: 'pager', label: 'Pager'},
    {key: 'street', label: 'Straße'},
    {key: 'city', label: 'Stadt'},
    {key: 'postal_code', label: 'PLZ'},
    {key: 'state', label: 'Bundesland'},
    {key: 'country', label: 'Land'},
    {key: 'home_street', label: 'Privat Straße'},
    {key: 'home_city', label: 'Privat Stadt'},
    {key: 'home_postal_code', label: 'Privat PLZ'},
    {key: 'home_state', label: 'Privat Bundesland'},
    {key: 'home_country', label: 'Privat Land'},
    {key: 'business_homepage', label: 'Webseite'},
    {key: 'personal_notes', label: 'Notizen'},
    {key: 'birthday', label: 'Geburtstag'},
  ];
  return fields
    .map(({key, label}) => {
      const oldVal = String(contact[key] ?? '');
      const newVal = String(proposed[key] ?? '');
      if (contact.is_pending_create) {
        if (newVal) return {label, oldVal: '', newVal};
        return null;
      }
      if (oldVal !== newVal && (oldVal || newVal)) {
        return {label, oldVal, newVal};
      }
      return null;
    })
    .filter(Boolean) as Array<{label: string, oldVal: string, newVal: string}>;
}

function getDisplayName(c: ContactWithPending) {
  if (c.display_name) return c.display_name;
  const parts = [c.title, c.first_name, c.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(' ');
  const company = c.company_name ? (c.department ? `${c.company_name} — ${c.department}` : c.company_name) : '';
  return company || c.email_address || 'Unbenannter Kontakt';
}

function ContactCard({ contact, showCheckboxes, selectedIds, toggleCheckbox }: {
  contact: ContactWithPending;
  showCheckboxes: boolean;
  selectedIds: Set<number>;
  toggleCheckbox: (id: number) => void;
}) {
  const changedFields = getChangedFields(contact);
  const hasPending = changedFields.length > 0 || contact.latest_pending_action === 'delete';

  return (
    <div
      className={`bg-white rounded-lg border p-3 hover:shadow-md transition-shadow ${
        hasPending ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        {showCheckboxes && (
          <input
            type="checkbox"
            checked={selectedIds.has(contact.id)}
            onChange={() => toggleCheckbox(contact.id)}
            className="mt-1 w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
          />
        )}
        {/* Detail-Spalte: Plain-Klick navigiert; Modifikator-/Mittelklick bleibt nativ */}
        <Link to={`/contacts/${contact.id}`} className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-gray-900 text-base">
              {getDisplayName(contact)}
            </span>
            {(contact.pending_proposals_count ?? 0) > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">
                {(contact.pending_proposals_count ?? 0)} Änderung{(contact.pending_proposals_count ?? 0) > 1 ? 'en' : ''}
              </span>
            )}
            {contact.is_pending_create && (
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Neu
              </span>
            )}
            {contact.latest_pending_action === 'delete' && (
              <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium">
                Löschung beantragt
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            {contact.company_name && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5 text-gray-400" />
                {contact.company_name}
              </span>
            )}
            {contact.email_address && (
              <a href={`mailto:${contact.email_address}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:underline">
                <Mail className="w-3.5 h-3.5 text-gray-400" />
                {contact.email_address}
              </a>
            )}
            {contact.mobile_phone && (
              <a href={`tel:${contact.mobile_phone.replace(/\s/g, '')}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {contact.mobile_phone}
              </a>
            )}
            {contact.business_phone && (
              <a href={`tel:${contact.business_phone.replace(/\s/g, '')}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {contact.business_phone} (G)
              </a>
            )}
            {contact.home_phone && (
              <a href={`tel:${contact.home_phone.replace(/\s/g, '')}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {contact.home_phone} (P)
              </a>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1">
            {contact.street && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${contact.street} ${contact.postal_code || ''} ${contact.city || ''}`.trim())}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                <MapPin className="w-3.5 h-3.5" />
                {contact.street}{contact.postal_code && `, ${contact.postal_code}`}{contact.city && ` ${contact.city}`}
              </a>
            )}
            {contact.home_street && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${contact.home_street} ${contact.home_postal_code || ''} ${contact.home_city || ''}`.trim())}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-green-600 hover:underline">
                <MapPin className="w-3.5 h-3.5" />
                {contact.home_street}{contact.home_postal_code && `, ${contact.home_postal_code}`}{contact.home_city && ` ${contact.home_city}`}
              </a>
            )}
            {contact.other_street && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${contact.other_street} ${contact.other_postal_code || ''} ${contact.other_city || ''}`.trim())}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-orange-600 hover:underline">
                <MapPin className="w-3.5 h-3.5" />
                {contact.other_street}{contact.other_postal_code && `, ${contact.other_postal_code}`}{contact.other_city && ` ${contact.other_city}`}
              </a>
            )}
          </div>

          {changedFields.length > 0 && (
            <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200 text-sm space-y-1">
              <p className="text-amber-800 font-medium text-xs uppercase tracking-wide">Ausstehende Änderungen:</p>
              {changedFields.map(({ label, oldVal, newVal }) => (
                <div key={label} className="flex gap-2 text-amber-900">
                  <span className="font-medium min-w-[120px]">{label}:</span>
                  {oldVal && <span className="line-through text-gray-500">{oldVal}</span>}
                  <span className="font-medium">{newVal}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {contact.updated_at ? formatTimeAgo(contact.updated_at) : ''}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </Link>
      </div>
    </div>
  );
}

function matchesLetter(contact: ContactWithPending, letter: string): boolean {
  if (!letter) return true;
  const ln = (contact.last_name || '').trim();
  const fn = (contact.first_name || '').trim();
  const check = ln || fn;
  if (!check) return false;
  return check.toUpperCase().startsWith(letter.toUpperCase());
}

/**
 * Kontakte-Listenspalte: Root = eigener Scroll-Container (IntersectionObserver
 * mit root auf diese Spalte). Öffnet Kontakte über die Route in der
 * Detail-Spalte — nicht mehr im Split-View-Panel.
 */
export function ContactsList() {
  const [contacts, setContacts] = useState<ContactWithPending[]>([]);
  const [pendingCreates, setPendingCreates] = useState<ContactWithPending[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState<SortOption>(() => {
    const saved = localStorage.getItem(SORT_STORAGE_KEY);
    return (saved as SortOption) || 'name_asc';
  });
  const [letter, setLetter] = useState<string>('');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);

  // Debounce search — nur bei tatsächlicher Änderung zurücksetzen; sonst
  // löscht der Mount-Timer die initial geladene Liste und nichts refetcht.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== debouncedSearch) {
        setDebouncedSearch(search);
        setPage(1);
        setContacts([]);
        setPendingCreates([]);
        setHasMore(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Sequenznummer gegen Races: Antworten veralteter Reloads verwerfen
  // (Muster wie in BauvorhabenList).
  const reloadSeq = useRef(0);
  // Expliziter Reload-Trigger: wenn ein Handler die Liste zurücksetzt,
  // ohne dass eine loadContacts-Abhängigkeit ihren Wert ändert (z. B.
  // Bulk-Löschen bei page===1), würde sonst nichts neu laden.
  const [reloadNonce, setReloadNonce] = useState(0);

  // Load contacts
  const loadContacts = useCallback(async () => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);
    const seq = ++reloadSeq.current;

    try {
      const response = await contactsApi.getAll(page, debouncedSearch, sort, letter);
      if (seq !== reloadSeq.current) return;
      const newContacts = response.data.data as ContactWithPending[];
      const pagination = response.data.pagination;

      const pending = newContacts
        .filter(c => c.is_pending_create)
        .filter(c => matchesLetter(c, letter));
      const regular = newContacts.filter(c => !c.is_pending_create);

      if (page === 1) {
        setPendingCreates(pending);
        setContacts(regular);
      } else {
        setContacts(prev => [...prev, ...regular]);
      }

      const totalPages = pagination?.total_pages ?? 1;
      const total = pagination?.total ?? 0;
      setTotalCount(total);
      setHasMore(page < totalPages && newContacts.length > 0);
    } catch (err) {
      if (seq !== reloadSeq.current) return;
      setError('Fehler beim Laden der Kontakte');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [page, debouncedSearch, sort, letter, reloadNonce]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const refetchFromStart = useCallback(() => {
    setPage(1);
    setContacts([]);
    setPendingCreates([]);
    setHasMore(true);
    setReloadNonce(n => n + 1);
  }, []);

  // IntersectionObserver für Infinite Scroll — root auf die eigene
  // Spalte (im Spalten-Layout scrollt die Liste, nicht das Dokument).
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Nur nachladen, wenn die Liste tatsächlich Zeilen zeigt — sonst
        // feuert der Observer auf der leeren Spalte und holt sinnlose
        // Folgeseiten (klassisches „Liste verschwindet"-Artefakt).
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore && contacts.length > 0) {
          setPage(prev => prev + 1);
        }
      },
      { root, threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, contacts.length]);

  // Close sort dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSortChange = (option: SortOption) => {
    setSort(option);
    localStorage.setItem(SORT_STORAGE_KEY, option);
    setShowSortDropdown(false);
    refetchFromStart();
  };

  const handleLetterClick = (l: string) => {
    if (letter === l) {
      setLetter('');
    } else {
      setLetter(l);
    }
    refetchFromStart();
  };

  const clearLetter = () => {
    setLetter('');
    refetchFromStart();
  };

  const toggleCheckbox = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const allIds = [...contacts, ...pendingCreates].map(c => c.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    const newSet = new Set(selectedIds);
    if (allSelected) {
      allIds.forEach(id => newSet.delete(id));
    } else {
      allIds.forEach(id => newSet.add(id));
    }
    setSelectedIds(newSet);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Möchten Sie ${selectedIds.size} Kontakte wirklich löschen?`)) return;
    setIsBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        await contactsApi.delete(id);
      }
      setSelectedIds(new Set());
      setShowCheckboxes(false);
      refetchFromStart();
    } catch {
      setError('Fehler beim Löschen');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const allVisibleContacts = [...pendingCreates, ...contacts];

  return (
    <div ref={scrollRef} className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      {/* Sticky header bar */}
      <div
        className="sticky top-0 z-10 px-3"
        style={{
          maskImage: 'linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)',
        }}
      >
        <div className="py-3 space-y-3 bg-gray-50/95 backdrop-blur-sm">
          {/* Title row */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-600" />
              <h1 className="text-lg font-bold text-gray-900">Kontakte</h1>
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium">
                {totalCount}
              </span>
            </div>
            <Link
              to="/contacts/new"
              className="inline-flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Neu
            </Link>
          </div>

          {/* Search and controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-sm"
              >
                <ArrowUpDown className="w-4 h-4" />
                {sortLabels[sort]}
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {(Object.keys(sortLabels) as SortOption[]).map((option) => (
                    <button
                      key={option}
                      onClick={() => handleSortChange(option)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                        sort === option ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {sortLabels[option]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowUpload(!showUpload)}
              className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-sm"
            >
              <Upload className="w-4 h-4" />
              Importieren
            </button>

            <button
              onClick={() => {
                setShowCheckboxes(!showCheckboxes);
                if (showCheckboxes) setSelectedIds(new Set());
              }}
              className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
                showCheckboxes
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}
            >
              <CheckSquare className="w-4 h-4" />
              Auswählen
            </button>
          </div>

          {/* Bulk delete bar */}
          {showCheckboxes && (
            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
              <button
                onClick={selectAllVisible}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Alle sichtbaren
              </button>
              {selectedIds.size > 0 && (
                <>
                  <span className="text-sm text-gray-500">{selectedIds.size} ausgewählt</span>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Löschen
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setShowCheckboxes(false);
                  setSelectedIds(new Set());
                }}
                className="ml-auto text-sm text-gray-500 hover:text-gray-700"
              >
                Abwählen
              </button>
            </div>
          )}

          {/* Letter filter */}
          <div className="flex flex-wrap items-center gap-1">
            {ALPHABET.map((l) => (
              <button
                key={l}
                onClick={() => handleLetterClick(l)}
                className={`w-6 h-6 text-[10px] font-medium rounded transition-colors ${
                  letter === l
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {l}
              </button>
            ))}
            {letter && (
              <button
                onClick={clearLetter}
                className="w-6 h-6 flex items-center justify-center text-red-500 hover:text-red-700 border border-red-300 rounded bg-red-50 hover:bg-red-100 transition-colors"
                title="Filter zurücksetzen"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Upload section */}
      {showUpload && (
        <div className="m-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4 relative">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">Importieren</h2>
            <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <VcfUpload onImportSuccess={() => { setShowUpload(false); refetchFromStart(); }} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg mx-3">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Contact list */}
      <div className="space-y-2 px-3 pb-4">
        {/* Pending creates section */}
        {pendingCreates.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <FileClock className="w-4 h-4 text-amber-600" />
              <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">
                Ausstehende Genehmigung ({pendingCreates.length})
              </h2>
            </div>
            {pendingCreates.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                showCheckboxes={showCheckboxes}
                selectedIds={selectedIds}
                toggleCheckbox={toggleCheckbox}
              />
            ))}
          </div>
        )}

        {/* Regular contacts */}
        {isLoading && page === 1 && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        )}

        {!isLoading && allVisibleContacts.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Keine Kontakte gefunden
          </div>
        )}

        {contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            showCheckboxes={showCheckboxes}
            selectedIds={selectedIds}
            toggleCheckbox={toggleCheckbox}
          />
        ))}

        {/* Infinite scroll loader */}
        <div ref={loaderRef} className="py-4 flex items-center justify-center">
          {isLoadingMore && (
            <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
          )}
          {!hasMore && contacts.length > 0 && (
            <span className="text-sm text-gray-400">Ende der Liste</span>
          )}
        </div>
      </div>
    </div>
  );
}